import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import FocusTrap from 'focus-trap-react';

// components
import Button from '/react4xp/components//Button';
import Guidelines from '/react4xp/components//Guidelines';
import Clock from '/react4xp/components//Clock';
import SearchInput from '/react4xp/components//SearchInput';

// utisl
import { tableWrapper } from '/lib/reactutils/utils';

interface HeaderProps {
    bgColor?: 'transparent' | 'primary' | 'white' | string;
    theme: 'light' | 'dark'
    isPrototype?: boolean;
    skipToMain: string;
    backText: string;
    backLink?: string;
    logoSvg: string;
    logoHref: string;
    logoAriaLabel: string;
    searchHref: string;
    searchText: string;
    menuOpen: string;
    menuClose: string;
    menuFirstItems: Array<{ name: string; link: string; external?: boolean; subTopics: Array<{ name: string; link: string; external?: boolean }> }>;
    menuSecondItems: Array<{ name: string, link: string, external?: boolean }>;
    clocks: Array<{ label: string, zone: string, background: string }>;
    custormerPortal: { name: string, link: string, external?: boolean };
}

export default function Header({
    bgColor,
    theme,
    isPrototype,
    skipToMain,
    backText,
    backLink,
    logoSvg,
    logoHref,
    logoAriaLabel,
    searchHref,
    searchText,
    menuOpen,
    menuClose,
    menuFirstItems,
    menuSecondItems,
    clocks,
    custormerPortal
}: HeaderProps) {

    // table wrapper function
    tableWrapper()

    const searchInputRef = useRef<HTMLInputElement>(null);
    const [isMenuShown, setIsMenuShown] = useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [menuColor, setMenuColor] = useState(bgColor || '');
    const [scrollPosition, setScrollPosition] = useState(0);
    const [firstSectionBgColor, setFirstSectionBgColor] = useState('');
    const [isDarkColor, setIsDarkColor] = useState(false);

    const openCloseMenu = () => {
        if (!isMenuShown && isSearchActive) {
            setIsSearchActive(false); // Close the search if it's open
        }
        setScrollPosition(window.scrollY); // Save current scroll position
        setIsMenuShown(prev => {
            const newState = !prev;
            if (newState) {
                window.document.body.style.overflowY = 'scroll';
                //window.document.body.style.position = 'fixed';
                //window.document.body.style.top = `-${scrollPosition}px`; // Set top position to prevent scroll jump
            } else {
                window.document.body.style.overflowY = 'auto';
                //window.document.body.style.position = '';
                //window.document.body.style.top = '';
                //window.scrollTo(0, scrollPosition); // Restore scroll position
            }
            return newState;
        });
    };

    const openCloseSearch = (event: React.MouseEvent) => {
        event.preventDefault(); // Prevent default behavior
        if (!isSearchActive && isMenuShown) {
            setIsMenuShown(false); // Close the menu if it's open
        }
        setScrollPosition(window.scrollY); // Save current scroll position
        setIsSearchActive(prev => {
            const newState = !prev;
            if (newState) {
                window.document.body.style.overflowY = 'scroll';
                //window.document.body.style.position = 'fixed';
                //window.document.body.style.top = `-${scrollPosition}px`; // Set top position to prevent scroll jump
            } else {
                window.document.body.style.overflowY = 'auto';
                //window.document.body.style.position = '';
                //window.document.body.style.top = '';
                //window.scrollTo(0, scrollPosition); // Restore scroll position
            }
            return newState;
        });
    };

    useEffect(() => {
        let lastScrollTop = 0;
    
        const handleScroll = () => {
            if (!isMenuShown && !isSearchActive) {
                const menu = window.document.getElementById('j-menu');
                const header = window.document.querySelector('header'); // Adjust this to your header element
    
                // Show/hide header based on scroll direction
                const isMobile = window.matchMedia('(max-width: 767px)').matches;
                let currentScroll = 0;
                if (isMobile) {
                    currentScroll = window.pageYOffset || window.document.documentElement.scrollTop;
                }
    
                if (currentScroll > lastScrollTop) {
                    // Scrolling down
                    header.classList.add('j-menu--hidden');
                } else {
                    // Scrolling up
                    header.classList.remove('j-menu--hidden');
                    
                }
    
                lastScrollTop = currentScroll <= 0 ? 0 : currentScroll; // Prevent negative scroll values
    
                // Manage j-menu class
                if (window.scrollY > 0) {
                    menu.classList.add('j-menu--expanded');
                } else {
                    menu.classList.remove('j-menu--expanded');
                }
    
                updateMenuColor();
            }
        };

        const menu = window.document.getElementById('j-menu');
        if (isMenuShown || isSearchActive) {
            menu.classList.remove('j-menu--expanded');
        } else if (window.scrollY > 0) {
            menu.classList.add('j-menu--expanded');
        } else {
            menu.classList.remove('j-menu--expanded');
        }

        const updateMenuColor = () => {
            const sections = window.document.querySelectorAll('section');
            let currentBgColor = '';

            sections.forEach(section => {
                const rect = section.getBoundingClientRect();
                const isMobile = window.matchMedia('(max-width: 767px)').matches;
                const isTablet = window.matchMedia('(min-width: 768px)').matches;
                const measureMobile = isMobile && 60 ;
                const measureTablet = isTablet && 36;

                if (rect.top <= measureMobile && rect.bottom >= 0) {
                    currentBgColor = window.getComputedStyle(section).backgroundColor;
                }

                if (rect.top <= measureTablet && rect.bottom >= measureTablet) {
                    currentBgColor = window.getComputedStyle(section).backgroundColor;
                } 
            });

            if (currentBgColor) {
                setMenuColor(currentBgColor);
            } else {
                setMenuColor('transparent');
            }
        };
        

        window.addEventListener('scroll', handleScroll);
        updateMenuColor();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMenuShown, isSearchActive]);

    useEffect(() => {
        setFirstSectionBgColor(window.document.querySelector('section') ? window.getComputedStyle(window.document.querySelector('section')).backgroundColor : '')
        setIsDarkColor([
            '#001F42',
            '#002855',
            'rgb(0, 31, 66)'
        ].includes(firstSectionBgColor) || ['#001F42', '#002855'].includes(rgbToHex(firstSectionBgColor)))
        
        history.scrollRestoration = 'manual'; // Prevent automatic scroll restoration

        return () => {
            history.scrollRestoration = 'auto'; // Restore default behavior on cleanup
        };
    }, []);

    useEffect(() => {
        if (isSearchActive && searchInputRef.current) {
            searchInputRef.current.focus(); // Focus the input field
        }
    }, [isSearchActive]);

    const rgbToHex = (rgb: string) => {
        const rgbValues = rgb.match(/\d+/g);
        return rgbValues ? `#${rgbValues.map(x => parseInt(x).toString(16).padStart(2, '0')).join('')}`.toUpperCase() : '';
    };

    const isActiveColor = [
        '#001F42',
        '#002855',
        'rgb(0, 31, 66)'
    ].includes(menuColor) || ['#001F42', '#002855'].includes(rgbToHex(menuColor));


    return (
        <>
            {isPrototype && <Guidelines />}

            <Button
                href='#main'
                className='skip-to-main'
            >
                {skipToMain}
            </Button>

            <FocusTrap
                active={isMenuShown}
                focusTrapOptions={{
                    escapeDeactivates: false,
                    clickOutsideDeactivates: false
                }}
            >

                <header
                    id='j-menu'
                    className={clsx(
                        'j-menu j-menu--success-stories',
                        theme === 'dark' && 'j-menu--success-stories--dark',
                        theme === 'light' && 'j-menu--success-stories--light'
                    )}
                    style={menuColor ? { backgroundColor: menuColor } : null}
                >
                    <div className={clsx(
                        'grid-container relative j-menu__navbar',
                        (isMenuShown || isSearchActive) && 'j-menu__navbar--active',
                        isActiveColor && 'j-menu__navbar--light',
                        isDarkColor && 'j-menu__navbar--light'
                    )} >
                        <div className="grid-row">
                            <div className="c-12 j-menu__wrapper">

                                <a
                                    className={clsx('j-menu__logo')}
                                    href={backLink || '#'}
                                    id='top-menu-logo'
                                >
                                    <svg width="14" height="12" viewBox="0 0 14 12" xmlns="http://www.w3.org/2000/svg" fill='currentColor'><path d="M1.2998 5.2998H13.1998V6.6998H1.2998V5.2998Z"/><path d="M6.98967 1.49972L2.48966 5.99971L6.98967 10.4997L5.99971 11.4897L0.509766 5.99971L5.99971 0.509766L6.98967 1.49972Z" /></svg> <span>{backText}</span>
                                </a>

                                <a
                                    className={clsx('j-menu__logo-center')}
                                    href={logoHref}
                                    aria-label={logoAriaLabel}
                                    id='top-menu-logo-center'
                                >
                                    <span dangerouslySetInnerHTML={{ __html: logoSvg }} suppressHydrationWarning />
                                </a>

                                <div className='j-menu__actions'>
                                    <div className='j-menu__icons'>

                                        <Button
                                            href={searchHref}
                                            rightIcon={isSearchActive ? 'Times' : 'Search'}
                                            className={clsx('j-menu__search-button', isSearchActive && 'active j-menu__closeSearch')}
                                            menuBtn='pages'
                                            onClick={openCloseSearch}
                                            ariaLabel='Open search'
                                        />

                                        <Button
                                            className={clsx('j-menu__hamburguer', isMenuShown && 'active j-menu__closeHamburguer')}
                                            rightIcon={isMenuShown ? 'Times' : 'Hamburguer'}
                                            menuBtn='pages'
                                            onClick={openCloseMenu}
                                            ariaLabel='Open menu'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div id='j-menu__drawer' className={clsx('j-menu__drawer', isMenuShown ? 'j-menu__drawer--active bgcolor' : '')}>
                        <div className='grid-container'>
                            <div className='grid-row j-menu__inner-drawer'>
                                <div className="c-12 c-md-4 j-menu__items">
                                    {menuFirstItems.map((topic, i) => (
                                        <div key={i} className='j-menu__primaries'>
                                            
                                            <Button
                                                href={topic.link}
                                                target={topic.external}
                                                menuBtn='topic'>
                                                {topic.name}
                                            </Button>

                                            {topic.subTopics.length > 0 && (
                                                <ul className='j-menu__sub-items'>
                                                    {topic.subTopics.map((subTopic, j) => (
                                                        <li key={j}>
                                                            <Button
                                                                href={subTopic.link}
                                                                target={subTopic.external}
                                                                menuBtn='sub-topic'
                                                            >
                                                                {subTopic.name}
                                                            </Button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="c-xs-6 start-md-5 c-md-3 j-menu__items">
                                    <ul className='j-menu__secondary-items'>
                                        {menuSecondItems.slice(0, 4).map((item, i) => (
                                            <li key={i}>
                                                <Button
                                                    href={item.link}
                                                    target={item.external}
                                                    menuBtn='sub-topic'
                                                >
                                                    {item.name}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="c-xs-6 c-md-3 j-menu__items">
                                    <ul className='j-menu__secondary-items'>
                                        {menuSecondItems.slice(4, 8).map((item, i) => (
                                            <li key={i}>
                                                <Button
                                                    href={item.link}
                                                    target={item.external}
                                                    menuBtn='sub-topic'
                                                >
                                                    {item.name}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {
                                    custormerPortal.name &&
                                    <div className="c-12 c-md-2 j-menu__items j-menu__button">
                                        <Button
                                            color='light'
                                            size='md'
                                            className='btn__nobreak'
                                            rightIcon=''
                                            href={custormerPortal.link}
                                            target={custormerPortal.external}
                                        >
                                            {custormerPortal.name}
                                        </Button>
                                    </div>
                                }
                                <div className="c-md-8 j-menu__divider">

                                </div>
                                <div className="start-md-5 c-md-8 j-menu__clocks">
                                    {clocks.map((clock, i) => (
                                        <Clock
                                            key={i}
                                            background={clock.background}
                                            label={clock.label}
                                            zone={clock.zone}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search */}

                    <div id='j-menu__search' className={clsx('j-menu__search', isSearchActive && 'j-menu__search--active')}>
                        <div className='grid-container'>
                            <div className='grid-row j-menu__search-inner'>
                                <div className="c-12 j-menu__search-form">
                                    <SearchInput
                                        ref={searchInputRef} // Pass the ref to SearchInput
                                        className='j-menu__search-input'
                                        leftIcon='Search'
                                        rightIcon='ArrowRight'
                                        placeholder='Search'
                                        href={searchHref}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                </header>

            </FocusTrap>
        </>
    );
}

